import React, { useState } from 'react';
import { socialWallApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Send, Edit, Trash2, MoreVertical, X } from 'react-feather';
import { Button } from '../ui/button';
import './SocialWall.css';

const CommentSection = ({
  postId,
  comments,
  onCommentAdded,
  onCommentUpdated,
  onCommentDeleted
}) => {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const comment = await socialWallApi.addComment(postId, newComment.trim());
      onCommentAdded(postId, comment);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
    setShowMenuFor(null);
  };

  const handleSaveEdit = async (commentId) => {
    try {
      const updatedComment = await socialWallApi.updateComment(commentId, editContent.trim());
      onCommentUpdated(postId, commentId, updatedComment);
      setEditingCommentId(null);
      setEditContent('');
    } catch (err) {
      console.error('Error updating comment:', err);
      alert('Failed to update comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await socialWallApi.deleteComment(commentId);
      onCommentDeleted(postId, commentId);
      setShowMenuFor(null);
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="comment-section">
      {comments.length > 0 && (
        <div className="comments-list">
          {comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-author-avatar">
                {comment.user?.photo_url ? (
                  <img src={comment.user.photo_url} alt={comment.user.display_name} />
                ) : (
                  <div className="comment-author-initials">
                    {comment.user?.display_name?.charAt(0) || 'U'}
                  </div>
                )}
              </div>
              <div className="comment-content-wrapper">
                <div className="comment-header">
                  <span className="comment-author-name">
                    {comment.user?.display_name || 'Unknown User'}
                  </span>
                  <span className="comment-date">{formatDate(comment.created_at)}</span>
                </div>
                {editingCommentId === comment.id ? (
                  <div className="comment-edit">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="comment-edit-textarea"
                      rows={2}
                    />
                    <div className="comment-edit-actions">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleSaveEdit(comment.id)}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditContent('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-text">{comment.content}</div>
                )}
              </div>
              {comment.user_id === user?.id && editingCommentId !== comment.id && (
                <div className="comment-menu">
                  <button
                    className="comment-menu-button"
                    onClick={() => setShowMenuFor(showMenuFor === comment.id ? null : comment.id)}
                  >
                    <MoreVertical size={14} />
                  </button>
                  {showMenuFor === comment.id && (
                    <div className="comment-menu-dropdown">
                      <button
                        className="comment-menu-item"
                        onClick={() => handleEdit(comment)}
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        className="comment-menu-item delete"
                        onClick={() => handleDelete(comment.id)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                      <button
                        className="comment-menu-item"
                        onClick={() => setShowMenuFor(null)}
                      >
                        <X size={14} />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form className="comment-form" onSubmit={handleSubmit}>
        <div className="comment-form-avatar">
          {user?.photoURL ? (
            <img src={user.photoURL} alt={user.displayName} />
          ) : (
            <div className="comment-form-initials">
              {user?.displayName?.charAt(0) || 'U'}
            </div>
          )}
        </div>
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          className="comment-input"
          disabled={isSubmitting}
        />
        <button
          type="submit"
          className="comment-submit-button"
          disabled={!newComment.trim() || isSubmitting}
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export default CommentSection;

